import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import OTPInput from "react-otp-input";
import { toast } from "react-toastify";
import { ArrowLeft, BadgeCheck, CalendarDays, Check, ChevronDown, Home, House, Laptop, SearchX, Users } from "lucide-react";
import callApi from "../../utility/apiCaller";
import { login, setLead } from "../../store/app/appReducer";
import { setUserClickData } from "../../utility/setUserClickData";
import "./BusinessLoanV2.css";

const loanOptions = [
  { amount: "50K-2L", label: "₹50K – ₹2L", text: "Working capital support" },
  { amount: "2L-5L", label: "₹2L – ₹5L", text: "Business growth" },
  { amount: "5L-10L", label: "₹5L – ₹10L", text: "Expansion funding" },
  { amount: "10L-25L", label: "₹10L – ₹25L", text: "Scale operations" },
  { amount: "25L+", label: "₹25L+", text: "Large business requirement" },
];

const turnoverOptions = [
  { value: "5L", label: "Up to ₹5L" },
  { value: "5L-25L", label: "₹5L – ₹25L" },
  { value: "25L-50L", label: "₹25L – ₹50L" },
  { value: "50L-1Cr", label: "₹50L – ₹1 Cr" },
  { value: "1Cr-5Cr", label: "₹1 Cr – ₹5 Cr" },
  { value: "5Cr+", label: "₹5 Cr+" },
];

const companyTypeOptions = [
  "Sole Proprietorships",
  "Partnership Firms",
  "Private Limited Companies (Pvt Ltd)",
  "Limited Liability Partnerships (LLP)",
];

const businessTypeOptions = [
  "Consulting",
  "Transportation",
  "Hospitality",
  "E-commerce",
  "Retail stores",
  "Manufacturing",
  "Digital businesses",
  "Service based business",
];

const normalizeCompanyType = (value) => {
  const raw = String(value || "").trim();
  if (!raw || companyTypeOptions.includes(raw)) return raw;
  const key = raw.toLowerCase().replace(/[^a-z]/g, "");
  if (key.includes("llp") || key.includes("limitedliability")) return "Limited Liability Partnerships (LLP)";
  if (key.includes("private") || key.includes("pvt")) return "Private Limited Companies (Pvt Ltd)";
  if (key.includes("partnership")) return "Partnership Firms";
  if (key.includes("propriet")) return "Sole Proprietorships";
  return raw;
};

const parseGstAddress = (value) => {
  if (!value) return { line1: "", line2: "", pincode: "" };
  if (typeof value === "object") {
    const street = [value.bno, value.bnm, value.flno].filter(Boolean).join(", ");
    const locality = [value.st, value.loc, value.dst].filter(Boolean).join(", ");
    return { line1: street || locality, line2: street ? locality : "", pincode: value.pncd || "" };
  }
  const parts = String(value).split(",").map((part) => part.trim()).filter(Boolean);
  const pincode = (String(value).match(/\b\d{6}\b/) || [""])[0];
  const addressParts = parts.filter((part) => !/^\d{6}$/.test(part));
  const midpoint = Math.ceil(addressParts.length / 2);
  const line1 = addressParts.slice(0, midpoint).join(", ");
  return { line1, line2: addressParts.slice(midpoint).join(", "), pincode };
};

const goldLenderIds = new Set([
  "6a2818d365c271100d17551e",
  "69ea0b864084a68bdadc40bd",
  "69a2c7ab1f6f4ddfd5577314",
]);

const normalOfferPreview = [
  { _id: "normal-lt", lender_id: "normal-lt", lender_name: "L&T Finance", credit_limit: "₹8,00,000", emi: "₹17,240", tenure: "60 Months", interest_rate: "10.75% p.a." },
  { _id: "normal-axis", lender_id: "normal-axis", lender_name: "AXIS Finance", credit_limit: "₹7,50,000", emi: "₹16,820", tenure: "60 Months", interest_rate: "12.35% p.a." },
  { _id: "normal-tata", lender_id: "normal-tata", lender_name: "TATA Capital", credit_limit: "₹6,00,000", emi: "₹15,750", tenure: "60 Months", interest_rate: "15.75% p.a." },
];

const goldOfferPreview = [
  { _id: "gold-muthoot", lender_id: "6a2818d365c271100d17551e", lender_name: "Muthoot Finance", credit_limit: "₹8,00,000", emi: "₹17,240", tenure: "60 Months", interest_rate: "10.75% p.a.", logo: "/assets/img/muthoot629.png" },
  { _id: "gold-iifl", lender_id: "69ea0b864084a68bdadc40bd", lender_name: "IIFL Finance", credit_limit: "₹7,50,000", emi: "₹16,820", tenure: "60 Months", interest_rate: "11.00% p.a.", logo: "/assets/img/iifl626.png" },
];

const initialData = {
  mobile: "",
  fullName: "",
  gender: "",
  dob: "",
  pan: "",
  email: "",
  pincode: "",
  loanAmount: "",
  loanPurpose: "",
  annualTurnover: "",
  residenceType: "",
  officeType: "",
  gstAvailable: "",
  gst: "",
  msmeAvailable: "",
  msme: "",
  companyName: "",
  companyPan: "",
  companyType: "",
  companyPincode: "",
  incorporationDate: "",
  businessType: "",
  businessAddress: "",
  businessAddress2: "",
};

const formatDob = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
};

const formatPrefillDob = (value) => {
  const parts = String(value || "").split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  const spaced = formatDob(value);
  const digits = spaced.replace(/\D/g, "");
  return digits.length === 8 ? `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}` : spaced;
};

const apiDob = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 8 ? `${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}` : value;
};

const apiDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  const ymd = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (ymd) return `${ymd[1]}-${ymd[2].padStart(2, "0")}-${ymd[3].padStart(2, "0")}`;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const formatDateInput = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const isValidDate = (value) => {
  const match = String(value || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day && year >= 1900 && year <= 2100;
};

const isValidPan = (value) => /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(String(value || "").trim());

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());

const isValidPincode = (value) => /^\d{6}$/.test(String(value || "").trim());

const isValidGstin = (value) => /^[0-9A-Z]{15}$/.test(String(value || "").trim());

const normalizedGender = (value) => {
  const gender = String(value || "").toLowerCase();
  if (gender.startsWith("m")) return "male";
  if (gender.startsWith("f")) return "female";
  return gender ? "other" : "";
};

const prefillAddress = (address) => {
  if (!address) return "";
  if (address.full) return address.full;
  return [address.line_1, address.line_2, address.street_name, address.city, address.state, address.zip]
    .filter(Boolean)
    .join(" ");
};

const businessVintageMonths = (value) => {
  const iso = apiDate(value);
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  const now = new Date();
  const months = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
  return months >= 0 ? months : undefined;
};

const businessPayload = (mobile, data, payload) => ({
  businessloanlead: {
    contact_phone: mobile,
    otp_page: "multistep-bl-v1",
    contact_name: data.fullName || "NA",
    contact_email: data.email || undefined,
    pancard: data.pan || undefined,
    dob: apiDob(data.dob) || undefined,
    gender: data.gender || undefined,
    pincode: data.pincode || undefined,
    address1: data.address1 || undefined,
    amount_required: data.loanAmount || undefined,
    loan_purpose: data.loanPurpose || undefined,
    annual_turnover: data.annualTurnover || undefined,
    gst: data.gst || undefined,
    udyamno: data.msme || undefined,
    company_name: data.companyName || undefined,
    ownership: data.companyType || undefined,
    business_type: data.companyType || undefined,
    industry: data.businessType || undefined,
    business_pancard: data.companyPan || undefined,
    business_pincode: data.companyPincode || undefined,
    office_address_type: data.officeType || undefined,
    residence_type: data.residenceType || undefined,
    work_address1: data.businessAddress || undefined,
    work_address2: data.businessAddress2 || undefined,
    business_vintage: businessVintageMonths(data.incorporationDate),
    doi_udyam: apiDate(data.incorporationDate),
    ...payload,
  },
});
const GTM_CONTAINER_ID = "GTM-5NSJ2KQS";
const loadBusinessLoanGtm = () => {
  if (typeof window === "undefined" || !GTM_CONTAINER_ID) return;

  const scriptId = "gtm-" + GTM_CONTAINER_ID;
  if (document.getElementById(scriptId)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const script = document.createElement("script");
  script.id = scriptId;
  script.async = true;
  script.src =
    "https://www.googletagmanager.com/gtm.js?id=" + encodeURIComponent(GTM_CONTAINER_ID);
  document.head.appendChild(script);

  const noscriptId = scriptId + "-noscript";
  if (!document.getElementById(noscriptId)) {
    const noscript = document.createElement("noscript");
    noscript.id = noscriptId;
    noscript.innerHTML =
      '<iframe src="https://www.googletagmanager.com/ns.html?id=' +
      encodeURIComponent(GTM_CONTAINER_ID) +
      '" height="0" width="0" style="display:none;visibility:hidden"></iframe>';
    document.body.insertBefore(noscript, document.body.firstChild);
  }
};

export default function BusinessLoanV2() {
  const [params] = useSearchParams();
  const dispatch = useDispatch();
  const [screen, setScreen] = useState("mobile");
  const [data, setData] = useState(initialData);
  const [otp, setOtp] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendCounter, setResendCounter] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [token, setToken] = useState("");
  const [offers, setOffers] = useState([]);
  const [confirmOrigin, setConfirmOrigin] = useState(""); // "setup" | "verification" | "msme"
  const offerPollingRef = useRef(false);
  const leadIdRef = useRef("");
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingStepProgress, setLoadingStepProgress] = useState(0);
  const [moreLendersChecking, setMoreLendersChecking] = useState(false);
  const [stillChecking, setStillChecking] = useState(false);
  const pushRef = useRef(false);
  const pollTimerRef = useRef(null);
  const ipAddressRef = useRef(null);

  const affId = params.get("aff_id") || "";
  const utmSource = params.get("utm_source") || "";
  const utmMedium = params.get("utm_medium") || "";
  const utmTerm = params.get("utm_term") || "";

  const trackEvent = (eventName, userId) => {
    setUserClickData({
      event_name: eventName,
      user_id: userId || "unknown",
      affiliate_id: affId || "No Aff_id found",
      utm_source: utmSource || utmMedium,
      utm_term: utmTerm,
    });
  };

  const getIpAddress = () => {
    if (!ipAddressRef.current) {
      ipAddressRef.current = fetch("https://api.ipify.org?format=json")
        .then((response) => (response.ok ? response.json() : null))
        .then((result) => result?.ip || "")
        .catch(() => "");
    }
    return ipAddressRef.current;
  };

  useEffect(() => {
    document.title = "DigitMoney - Business Loan";
    loadBusinessLoanGtm();
    trackEvent("pageview-business-loan-page-v2", params.get("ld_id") || "");
    getIpAddress();
  }, []);

  useEffect(() => {
    if (screen !== "otp") return undefined;
    setResendCounter(30);
    setCanResend(false);
    const interval = setInterval(() => {
      setResendCounter((value) => {
        if (value <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [screen]);

  const updateData = (key, value) => {
    setData((previous) => ({ ...previous, [key]: value }));
  };

  const recordConsent = async () => {
    if (!/^\d{10}$/.test(data.mobile)) return;

    const ipAddress = (await getIpAddress()) || null;

    try {
      const response = await callApi(
        "v1/consent/create",
        "post",
        {
          contact_name: "",
          contact_phone: data.mobile,
          utm_source: params.get("utm_source")?.split("?")[0] || null,
          utm_medium: params.get("utm_medium") || null,
          aff_id: params.get("aff_id") || null,
          ip_address: ipAddress,
          kyc_consent_datetime: new Date().toISOString(),
          lead_type: "App/Web",
        },
        "core"
      );
      if (String(response?.status || "").toLowerCase() !== "success") {
        throw new Error("Consent failed");
      }
    } catch (error) {
      toast.error("Unable to record consent. Please try again.");
    }
  };

  const handleConsentChange = (checked) => {
    setConsent(checked);
    if (checked) recordConsent();
  };

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(data.mobile)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to the Terms of Use.");
      return;
    }
    try {
      setLoading(true);
      const response = await callApi(
        "v1/sms/send-otp",
        "post",
        {
          contact_phone: data.mobile,
          kyc_consent: true,
          website_kyc_consent: true,
          kyc_consent_datetime: new Date().toISOString(),
          website_kyc_consent_datetime: new Date().toISOString(),
        },
        "messaging"
      );
      if (response.status !== "Success") throw new Error("OTP failed");
      trackEvent("otp-send-business-loan-page-v2", data.mobile);
      setScreen("otp");
    } catch (error) {
      toast.error("Unable to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!canResend) return;
    try {
      setLoading(true);
      const response = await callApi(
        "v1/sms/send-otp",
        "post",
        { contact_phone: data.mobile, kyc_consent: true },
        "messaging"
      );
      if (response.status !== "Success") throw new Error("OTP failed");
      trackEvent("otp-resend-business-loan-page-v2", data.mobile);
      setResendCounter(30);
      setCanResend(false);
    } catch (error) {
      toast.error("Unable to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrefill = async () => {
    try {
      const response = await callApi("v1/lender/mobile-to-prefill", "post", { mobile_number: data.mobile }, "loan");
      const prefill = response?.data?.data;
      if (response?.status !== "Success" || !prefill) return {};
      return {
        fullName: prefill.full_name || "",
        pan: prefill.pan_number || "",
        email: prefill.email || "",
        gender: normalizedGender(prefill.gender),
        dob: prefill.dob ? formatPrefillDob(prefill.dob) : "",
        pincode: prefill.address?.zip || "",
        address1: prefillAddress(prefill.address),
      };
    } catch (error) {
      return {};
    }
  };

  const fetchGstinFromMobile = async () => {
    try {
      const response = await callApi("v1/lender/gstin-from-mobile", "post", { mobile_number: data.mobile }, "loan");
      const gstin = response?.data?.data?.gstin;
      if (response?.status !== "Success" || !gstin) return {};
      return { gst: gstin, gstAvailable: "Yes" };
    } catch (error) {
      return {};
    }
  };

  const fetchMsmeFromMobile = async () => {
    try {
      const response = await callApi("v1/lender/mobile-to-msme", "post", { mobile_number: data.mobile }, "loan");
      const details = response?.data?.data?.details;
      if (response?.status !== "Success" || !Array.isArray(details) || details.length === 0) return {};
      return { msme: details[0].msme_number || "", msmeAvailable: "Yes", companyName: details[0].enterprise_name || "" };
    } catch (error) {
      return {};
    }
  };

  const createLead = async (sourceData = data, payload = {}, authToken = token) => {
    try {
      const response = await callApi(
        "v1/businessloanlead/new-v1",
        "post",
        businessPayload(sourceData.mobile, sourceData, {
          aff_id: params.get("aff_id") || "",
          source: params.get("source") || "",
          utm_source: params.get("utm_source") || "",
          utm_medium: params.get("utm_medium") || "",
          kyc_consent: true,
          kyc_consent_datetime: new Date().toISOString(),
          ip_address: (await getIpAddress()) || undefined,
          ...payload,
        }),
        "core",
        authToken
      );
      if (response?.status !== "Success") return false;
      const lead = response?.data?.businessloanlead?.lead;
      if (lead) {
        leadIdRef.current = lead._id || "";
        dispatch(setLead(lead));
      }
      return lead || false;
    } catch (error) {
      toast.error("Unable to save your business details.");
      return false;
    }
  };

  const leadToData = (lead) => ({
    fullName: lead.contact_name || "",
    gender: lead.gender || "",
    dob: lead.dob ? formatPrefillDob(String(lead.dob).split("T")[0]) : "",
    pan: lead.pancard || "",
    email: lead.contact_email || "",
    pincode: lead.pincode || "",
    address1: lead.address1 || "",
    loanAmount: lead.amount_required || "",
    loanPurpose: lead.loan_purpose || "",
    annualTurnover: lead.annual_turnover || "",
    residenceType: lead.residence_type || "",
    officeType: lead.business_type || "",
    gstAvailable: lead.is_gst ? "Yes" : lead.gst_available === "yes" ? "Yes" : lead.gst ? "Yes" : "",
    gst: lead.gst || "",
    msmeAvailable: lead.is_msme ? "Yes" : lead.udyam_available === "yes" ? "Yes" : lead.udyamno ? "Yes" : "",
    msme: lead.udyamno || "",
    companyName: lead.company_name || "",
    companyPan: lead.business_pancard || "",
    companyType: lead.business_type || lead.ownership || "",
    companyPincode: lead.business_pincode || lead.pincode || "",
    incorporationDate: lead.doi_udyam ? formatPrefillDob(String(lead.doi_udyam).split("T")[0]) : "",
    businessType: lead.industry || "",
    businessAddress: lead.work_address1 || "",
    businessAddress2: lead.work_address2 || "",
  });

  const resumeFromLead = (authToken, returnedLead, prefilledData = data) => {
    if (!returnedLead?._id) return false;
    leadIdRef.current = returnedLead._id;
    dispatch(setLead(returnedLead));
    const merged = { ...prefilledData, ...leadToData(returnedLead), mobile: data.mobile };
    setData(merged);
    setToken(authToken);
    const completed = Number(returnedLead.steps_completed || 0);
    const companySaved = !!(returnedLead.company_name && returnedLead.business_pancard);
    if ((Number(returnedLead.offers_count || 0) > 0) || (completed >= 5 && companySaved)) {
      offerPollingRef.current = false;
      return "offers";
    }
    if (completed >= 4) {
      setScreen("loadingLookup");
      return "loadingLookup";
    }
    if (completed === 3) setScreen("setup");
    else if (completed === 2) setScreen("turnover");
    else if (completed === 1) setScreen("loan");
    else setScreen("identity");
    return true;
  };

  const resumeFromLeadAndFetchCompany = async (authToken, returnedLead, prefilledData = data) => {
    const target = resumeFromLead(authToken, returnedLead, prefilledData);
    if (target === "offers") {
      const initialFetch = await fetchOffersOnce();
      const settled = initialFetch.total > 0 && initialFetch.received >= initialFetch.total;
      if (initialFetch.offers.length > 0) {
        openOfferResults(initialFetch.offers);
        setMoreLendersChecking(!settled);
        if (!settled) startOfferPolling(false);
        return true;
      }
      if (settled) {
        setMoreLendersChecking(false);
        setScreen("noOffers");
        return true;
      }
      setScreen("loading");
      return true;
    }
    if (target !== "loadingLookup") return target;
    const merged = { ...prefilledData, ...leadToData(returnedLead), mobile: data.mobile };
    const [gstMatch, msmeMatch] = await Promise.all([fetchGstinFromMobile(), fetchMsmeFromMobile()]);
    if (gstMatch.gst) {
      const details = await fetchGstDetails(gstMatch.gst);
      if (details.companyName) {
        setData((previous) => ({ ...previous, gstAvailable: "Yes", gst: gstMatch.gst, ...details }));
        setConfirmOrigin("setup");
        setScreen("companyConfirm");
        return true;
      }
    }
    if (msmeMatch.msme) {
      const details = await fetchMsmeDetails(msmeMatch.msme);
      if (details.companyName) {
        setData((previous) => ({ ...previous, msmeAvailable: "Yes", msme: msmeMatch.msme, ...details }));
        setConfirmOrigin("setup");
        setScreen("companyConfirm");
        return true;
      }
    }
    if (merged?.gst) {
      const details = await fetchGstDetails(merged.gst);
      if (details.companyName) {
        setData((previous) => ({ ...previous, ...details }));
        setConfirmOrigin("verification");
        setScreen("companyConfirm");
        return true;
      }
    }
    if (merged?.msme) {
      const details = await fetchMsmeDetails(merged.msme);
      if (details.companyName) {
        setData((previous) => ({ ...previous, ...details }));
        setConfirmOrigin("msme");
        setScreen("companyConfirm");
        return true;
      }
    }
    setScreen("verification");
    return true;
  };

  const verifyOtp = async (value = otp) => {
    if (value.length !== 4 || loading) return;
    try {
      setLoading(true);
      const response = await callApi(
        "v1/sms/validate-otp",
        "post",
        { contact_phone: data.mobile, otp: value },
        "messaging"
      );
      if (response.status !== "Success") {
        toast.error("Invalid OTP. Please try again.");
        return;
      }
      const authToken = response.data?.token || "";
      trackEvent("otp-submit-business-loan-page-v2", data.mobile);
      const prefilled = await fetchPrefill();
      const nextData = { ...data, ...prefilled };
      setData(nextData);
      setToken(authToken);
      dispatch(login({ ...response.data?.customer, token: authToken }));
      localStorage.setItem("mobile", data.mobile);
      const savedLead = await createLead(nextData, { is_mobile_verify_completed: "true" }, authToken);
      if (!savedLead) return;
      const resumed = await resumeFromLeadAndFetchCompany(authToken, savedLead, nextData);
      if (!resumed) setScreen("identity");
    } catch (error) {
      toast.error("Unable to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const saveIdentity = async () => {
    if (!data.fullName || !data.dob || !data.pan || !data.email || !data.pincode) {
      toast.error("Please complete your details to continue.");
      return;
    }
    if (!data.gender) {
      toast.error("Please select your gender.");
      return;
    }
    if (!isValidDate(data.dob)) {
      toast.error("Please enter a valid date of birth in DD/MM/YYYY format.");
      return;
    }
    if (!isValidPan(data.pan)) {
      toast.error("Please enter a valid PAN (e.g. ABCDE1234F).");
      return;
    }
    if (!isValidEmail(data.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!isValidPincode(data.pincode)) {
      toast.error("Please enter a valid 6-digit pin code.");
      return;
    }
    setLoading(true);
    const saved = await createLead(data, { is_stage1_completed: "true", steps_completed: 1 });
    setLoading(false);
    if (saved) {
      trackEvent("identity-details-submit-business-loan-page-v2", data.mobile);
      setScreen("loan");
    }
  };

  const saveLoan = async () => {
    if (!data.loanAmount) return;
    setLoading(true);
    const saved = await createLead(data, { amount_required: data.loanAmount, loan_purpose: data.loanPurpose, steps_completed: 2 });
    setLoading(false);
    if (saved) {
      trackEvent("loan-amount-submit-business-loan-page-v2", data.mobile);
      setScreen("turnover");
    }
  };

  const saveTurnover = async () => {
    if (!data.annualTurnover) return;
    setLoading(true);
    const saved = await createLead(data, { annual_turnover: data.annualTurnover, is_stage2_completed: "true", steps_completed: 3 });
    setLoading(false);
    if (saved) {
      trackEvent("turnover-submit-business-loan-page-v2", data.mobile);
      setScreen("setup");
    }
  };

  const saveSetup = async () => {
    if (!data.residenceType || !data.officeType) return;
    setLoading(true);
    const [gstMatch, msmeMatch] = await Promise.all([fetchGstinFromMobile(), fetchMsmeFromMobile()]);
    const saved = await createLead(data, {
      residence_type: data.residenceType,
      office_address_type: data.officeType,
      is_gst: !!gstMatch.gst,
      is_msme: !!msmeMatch.msme,
      is_stage3_completed: true,
      steps_completed: 4,
    });
    if (!saved) {
      setLoading(false);
      return;
    }
    trackEvent("business-setup-submit-business-loan-page-v2", data.mobile);
    if (gstMatch.gst) {
      const details = await fetchGstDetails(gstMatch.gst);
      if (details.companyName) {
        setData((previous) => ({ ...previous, gstAvailable: "Yes", gst: gstMatch.gst, ...details }));
        setConfirmOrigin("setup");
        setLoading(false);
        setScreen("companyConfirm");
        return;
      }
    }
    if (msmeMatch.msme) {
      const details = await fetchMsmeDetails(msmeMatch.msme);
      if (details.companyName) {
        setData((previous) => ({ ...previous, msmeAvailable: "Yes", msme: msmeMatch.msme, ...details }));
        setConfirmOrigin("setup");
        setLoading(false);
        setScreen("companyConfirm");
        return;
      }
    }
    setLoading(false);
    setScreen("verification");
  };

  const fetchGstDetails = async (gst) => {
    try {
      const response = await callApi("v1/lender/gstin", "post", { id_number: gst, get_details: true }, "loan");
      const details = response?.data?.data;
      if (response?.status !== "Success" || (!details?.legal_name && !details?.business_name)) return {};
      const address = parseGstAddress(details.address);
      return {
        companyName: details.business_name || details.legal_name || "",
        companyPan: details.pan_number || gst.slice(2, 12),
        companyType: normalizeCompanyType(details.constitution_of_business),
        companyPincode: address.pincode,
        incorporationDate: details.date_of_registration || "",
        businessType: details.nature_of_core_business_activity_description || "",
        businessAddress: address.line1,
        businessAddress2: address.line2,
      };
    } catch (error) {
      return {};
    }
  };

  const fetchMsmeDetails = async (msme) => {
    try {
      const initResponse = await callApi("v1/lender/udyog-aadhaar/initialize", "post", { id_number: msme }, "loan");
      const clientId = initResponse?.data?.data?.client_id;
      if (initResponse?.status !== "Success" || !clientId) return {};
      let statusResponse;
      let details = null;
      for (let attempt = 0; attempt < 5; attempt += 1) {
        statusResponse = await callApi("v1/lender/udyog-aadhaar/status", "post", { client_id: clientId }, "loan");
        details = statusResponse?.data?.data?.main_details;
        if (statusResponse?.status === "Success" && details?.name_of_enterprise) break;
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (!details?.name_of_enterprise) return {};
      const street = [details.flat, details.name_of_building, details.road].filter(Boolean).join(", ");
      const locality = [details.village, details.block, details.city, details.state].filter(Boolean).join(", ");
      return {
        companyName: details.name_of_enterprise || "",
        companyType: normalizeCompanyType(details.organization_type),
        companyPincode: details.pin || "",
        incorporationDate: details.date_of_incorporation || details.date_of_commencement || "",
        businessType: details.major_activity || "",
        businessAddress: street || locality,
        businessAddress2: street ? locality : "",
      };
    } catch (error) {
      return {};
    }
  };

  const saveRegistrationChoice = (key, value) => {
    if (key === "gstAvailable") {
      setData((previous) => ({ ...previous, gstAvailable: value, msmeAvailable: "", msme: "" }));
      setScreen(value === "Yes" ? "verification" : "msme");
      return;
    }
    setData((previous) => ({ ...previous, msmeAvailable: value }));
    setScreen(value === "Yes" ? "msme" : "bothNo");
  };

  const saveRegistrationNumber = async (type) => {
    const value = type === "gst" ? data.gst : data.msme;
    if (!value) {
      toast.error(`Please enter your ${type === "gst" ? "GST" : "MSME"} number.`);
      return;
    }
    if (type === "gst" && !isValidGstin(value)) {
      toast.error("Please enter a valid 15-character GST number.");
      return;
    }
    setLoading(true);
    const details = type === "gst" ? await fetchGstDetails(value) : await fetchMsmeDetails(value);
    if (!details.companyName) {
      setLoading(false);
      toast.error(`Unable to verify this ${type === "gst" ? "GST" : "MSME"} number.`);
      return;
    }
    const nextData = { ...data, ...details, companyPincode: details.companyPincode || data.companyPincode || data.pincode || "" };
    setData(nextData);
    const effectivePincode = details.companyPincode || data.companyPincode || data.pincode || "";
    const effectiveAddress = details.businessAddress || "";
    const registrationFlags = {
      is_gst: type === "gst" ? true : data.gstAvailable === "Yes",
      is_msme: type === "msme" ? true : data.msmeAvailable === "Yes",
    };
    const registrationDate = apiDate(details.incorporationDate);
    const registrationBusiness = {
      ...registrationFlags,
      company_name: details.companyName,
      ownership: details.companyType,
      business_type: details.companyType,
      industry: details.businessType,
      business_pincode: effectivePincode,
      office_address_type: data.officeType,
      work_address1: effectiveAddress,
      work_address2: details.businessAddress2,
      doi_udyam: registrationDate,
    };
    const extraPayload =
      type === "gst"
        ? { gst: value, business_pancard: details.companyPan, pincode: effectivePincode, ...registrationBusiness }
        : { udyamno: value, pincode: effectivePincode, ...registrationBusiness };
    const saved = await createLead(nextData, extraPayload);
    setLoading(false);
    if (saved) {
      trackEvent("registration-lookup-submit-business-loan-page-v2", data.mobile);
      setConfirmOrigin(type === "gst" ? "verification" : "msme");
      setScreen("companyConfirm");
    }
  };

  const saveCompanyDetails = async () => {
    if (!data.companyName || !data.companyPan || !data.companyType || !data.companyPincode || !data.incorporationDate || !data.businessType || !data.businessAddress) {
      toast.error("Please complete all business details to continue.");
      return;
    }
    if (!isValidPan(data.companyPan)) {
      toast.error("Please enter a valid company PAN (e.g. ABCDE1234F).");
      return;
    }
    if (!isValidPincode(data.companyPincode)) {
      toast.error("Please enter a valid 6-digit pin code.");
      return;
    }
    if (!isValidDate(data.incorporationDate)) {
      toast.error("Please enter a valid date of incorporation in DD/MM/YYYY format.");
      return;
    }
    setLoading(true);
    const saved = await createLead(data, {
      company_name: data.companyName,
      business_pancard: data.companyPan,
      ownership: data.companyType,
      business_type: data.companyType,
      industry: data.businessType,
      pincode: data.companyPincode || data.pincode || "",
      business_pincode: data.companyPincode || "",
      office_address_type: data.officeType,
      doi_udyam: apiDate(data.incorporationDate),
      business_vintage: businessVintageMonths(data.incorporationDate),
      work_address1: data.businessAddress,
      work_address2: data.businessAddress2,
      gst_available: data.gstAvailable.toLowerCase(),
      gst: data.gst,
      udyam_available: data.msmeAvailable.toLowerCase(),
      udyamno: data.msme,
      is_gst: data.gstAvailable === "Yes",
      is_msme: data.msmeAvailable === "Yes",
      is_stage4_completed: true,
      steps_completed: 5,
    });
    setLoading(false);
    if (saved) {
      trackEvent("company-details-submit-business-loan-page-v2", data.mobile);
      setScreen("loading");
    }
  };

  const finishRegistration = async () => {
    setLoading(true);
    const saved = await createLead(data, {
      company_name: data.companyName,
      business_pancard: data.companyPan,
      ownership: data.companyType,
      business_type: data.companyType,
      industry: data.businessType,
      pincode: data.companyPincode || data.pincode || "",
      business_pincode: data.companyPincode || "",
      office_address_type: data.officeType,
      doi_udyam: apiDate(data.incorporationDate),
      business_vintage: businessVintageMonths(data.incorporationDate),
      work_address1: data.businessAddress,
      work_address2: data.businessAddress2,
      gst_available: data.gstAvailable.toLowerCase(),
      gst: data.gst,
      udyam_available: data.msmeAvailable.toLowerCase(),
      udyamno: data.msme,
      is_gst: data.gstAvailable === "Yes",
      is_msme: data.msmeAvailable === "Yes",
      is_stage4_completed: true,
      steps_completed: 5,
    });
    setLoading(false);
    if (saved) {
      trackEvent("company-confirm-submit-business-loan-page-v2", data.mobile);
      setScreen("loading");
    }
  };

  const normalizeOffer = (offer, index) => ({
    ...offer,
    _id: offer?._id || offer?.id || `offer-${index}`,
    lender_id: offer?.lender_id || offer?.lenderId || "",
    lender_name: offer?.lender_name || offer?.lenderName || "Lending partner",
    credit_limit: offer?.credit_limit || offer?.loan_amount || offer?.loanAmount || "₹5,00,000",
    emi: offer?.emi || offer?.monthly_emi || offer?.monthlyEmi || "₹12,500",
    tenure: offer?.tenure || offer?.tenure_months || offer?.tenureMonths || "60 Months",
    interest_rate: offer?.interest_rate || offer?.interestRate || offer?.interest || "11.00% p.a.",
    logo: offer?.logo || offer?.logo_image_url || offer?.logoImageUrl || "",
  });

  const classifyOffers = (responseOffers) => {
    const normalizedOffers = responseOffers.map(normalizeOffer);
    const hasNormalOffer = normalizedOffers.some((offer) => !goldLenderIds.has(String(offer.lender_id).toLowerCase()));
    return { offers: normalizedOffers, type: hasNormalOffer ? "normal" : "gold" };
  };

  const openOfferResults = (responseOffers) => {
    const previewType = params.get("offer_preview");
    const previewOffers = previewType === "gold" ? goldOfferPreview : previewType === "normal" ? normalOfferPreview : [];
    const result = classifyOffers(responseOffers.length > 0 ? responseOffers : previewOffers);
    if (result.offers.length === 0) return;
    const visibleOffers = result.type === "normal"
      ? result.offers.filter((offer) => !goldLenderIds.has(String(offer.lender_id).toLowerCase()))
      : result.offers;
    setOffers(visibleOffers);
    setScreen(result.type === "gold" ? "goldOffers" : "normalOffers");
  };

  const pushLeadToLenders = async () => {
    if (!leadIdRef.current || pushRef.current) return true;
    pushRef.current = true;
    try {
      const response = await callApi(
        "v1/lead/business-lead-multi-lender",
        "post",
        { lead_id: leadIdRef.current },
        "core",
        token
      );
      return response?.status === "Success";
    } catch (error) {
      console.error("Multi-lender push failed", error);
      return false;
    } finally {
      pushRef.current = false;
    }
  };

  const fetchOffersOnce = async () => {
    try {
      const response = await callApi(`v1/loan_offer/lead_id/${leadIdRef.current}`, "get", {}, "core", token);
      if (response?.status !== "Success") return { offers: [], total: 0, received: 0 };
      const fetchedOffers = Array.isArray(response?.data?.offers) ? response.data.offers : [];
      const leadData = response?.data?.lead || {};
      const total = Number(leadData.total_response || 0);
      const received = Number(leadData.all_responses || 0);
      return { offers: fetchedOffers, total, received };
    } catch (error) {
      console.error("Failed to fetch offers", error);
      return { offers: [], total: 0, received: 0 };
    }
  };

  const fetchLenderStatusOnce = async () => {
    try {
      const response = await callApi(`v1/lender_api_call/${leadIdRef.current}/get-fail-success-lenders`, "post", {}, "loan");
      if (String(response?.status || "").toLowerCase() !== "success") return [];
      const data = response?.data || {};
      const lenders = new Map();
      const track = (item) => {
        const name = item?.lender_name || "";
        if (!name) return;
        lenders.set(name, { lender_name: name, message: item?.message || "" });
      };
      (Array.isArray(data.FailListDetails) ? data.FailListDetails : []).forEach(track);
      (Array.isArray(data.DuplicateListDetails) ? data.DuplicateListDetails : []).forEach(track);
      return [...lenders.values()];
    } catch (error) {
      console.error("Failed to fetch lender responses", error);
      return [];
    }
  };

  const stopOfferPolling = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = null;
  };

  const startOfferPolling = async (pushFailed = false) => {
    if (offerPollingRef.current) return;
    offerPollingRef.current = true;

    let settled = false;
    const poll = async () => {
      const [offerState, lenderResponses] = await Promise.all([fetchOffersOnce(), fetchLenderStatusOnce()]);
      const { offers: fetchedOffers, total, received } = offerState;
      const allResponded = total > 0 && received >= total;
      const hasOffers = fetchedOffers.length > 0;
      const nothingInFlight = pushFailed && received === 0 && total === 0 && lenderResponses.length === 0 && !hasOffers;

      if (hasOffers) {
        const result = classifyOffers(fetchedOffers);
        const visibleOffers = result.type === "normal"
          ? result.offers.filter((offer) => !goldLenderIds.has(String(offer.lender_id).toLowerCase()))
          : result.offers;
        setOffers(visibleOffers);
        setMoreLendersChecking(!allResponded);
        setStillChecking(false);
        setScreen((current) => (current === "loading" ? (result.type === "gold" ? "goldOffers" : "normalOffers") : current));
      } else if (allResponded || nothingInFlight) {
        setMoreLendersChecking(false);
        setStillChecking(false);
        setScreen((current) => (current === "loading" ? "noOffers" : current));
      } else {
        setStillChecking(true);
      }

      if (allResponded || nothingInFlight) {
        settled = true;
        stopOfferPolling();
      }
    };

    await poll();
    if (!settled && !pollTimerRef.current) {
      pollTimerRef.current = setInterval(poll, 5000);
    }
  };

  const runLoadingSequence = async () => {
    if (!leadIdRef.current) {
      openOfferResults([]);
      return;
    }
    const initialFetch = await fetchOffersOnce();
    if (initialFetch.offers.length > 0) {
      await startOfferPolling(false);
      return;
    }
    const alreadyPushed = initialFetch.total > 0 || initialFetch.received > 0;
    let pushFailed = false;
    if (!alreadyPushed) {
      pushFailed = !(await pushLeadToLenders());
    }
    const loadingItems = [
      "Mobile Verified",
      "Income Profile Matched",
      "Comparing 150+ Loan Programs",
      "Finding Best Offers",
      "Checking Instant Approval Chances",
    ];
    for (let step = 0; step < loadingItems.length; step += 1) {
      setLoadingStep(step);
      setLoadingStepProgress(0);
      const tickInterval = 100;
      const totalTicks = 2000 / tickInterval;
      for (let tick = 0; tick < totalTicks; tick += 1) {
        setLoadingStepProgress((tick + 1) / totalTicks);
        await new Promise((resolve) => setTimeout(resolve, tickInterval));
      }
    }
    setLoadingStep(loadingItems.length);
    setLoadingStepProgress(1);
    await startOfferPolling(pushFailed);
  };

  const goBack = () => {
    const previous = {
      otp: "mobile",
      identity: "otp",
      loan: "identity",
      turnover: "loan",
      setup: "turnover",
      verification: "setup",
      msme: "verification",
      bothNo: "msme",
      companyConfirm:
        confirmOrigin === "setup"
          ? "setup"
          : data.gstAvailable === "Yes"
          ? "verification"
          : data.msmeAvailable === "Yes"
          ? "msme"
          : "bothNo",
      companyDetails: "bothNo",
    };
    setScreen(previous[screen] || "mobile");
    if (screen === "otp") setOtp("");
  };

  useEffect(() => {
    if (screen !== "loading") return undefined;
    runLoadingSequence();
    return undefined;
  }, [screen]);

  useEffect(() => () => stopOfferPolling(), []);

  return (
    <main className="business-v2-app">
      {screen === "mobile" && (
        <>
          <section className="business-v2-mobile business-v2-scroll-page">
            <img className="business-v2-logo" src="/assets/img/Footer%20logo.png" alt="digitmoney" />
            <h1>Get Business Loan Offers in Minutes</h1>
            <img className="business-v2-trust" src="/assets/img/group505.png" alt="Google rating 4.7 out of 5, trusted by 100,000+ borrowers" />
            <p className="business-v2-mobile-subtitle">Verify your mobile number to check eligibility.</p>
            <label className="business-v2-field business-v2-mobile-field" htmlFor="business-v2-mobile">
              <span>Mobile Number</span>
              <input
                id="business-v2-mobile"
                inputMode="numeric"
                maxLength="10"
                placeholder="10-digit mobile number"
                value={data.mobile}
                onChange={(event) => updateData("mobile", event.target.value.replace(/\D/g, ""))}
              />
            </label>
            <label className="business-v2-consent">
              <input type="checkbox" checked={consent} onChange={(event) => handleConsentChange(event.target.checked)} />
              <span>
                By submitting this form, you have read and agree to the <a href="https://digitmoney.in/terms">Credit Report Terms of Use</a>, <a href="https://digitmoney.in/terms">Terms of Use</a> &amp; <a href="https://digitmoney.in/privacy-policy">Privacy Policy</a> of DigitMoney &amp; its <a href="https://digitmoney.in/lenders">Lending Partners</a>
              </span>
            </label>
            <div className="business-v2-security">
              <span><img src="/assets/img/protected.png" alt="" /></span>
              <p>Your data is <strong>100% encrypted</strong> and never shared without your consent.</p>
            </div>
            <div className="business-v2-partners">
              <div className="business-v2-partners-track">
                <img src="/assets/bank_img/Group 527.png" alt="Our lending partners" />
                <img src="/assets/bank_img/Group 527.png" alt="" aria-hidden="true" />
              </div>
            </div>
            <p className="business-v2-proof">🔐 100% Secure&nbsp;&nbsp;&nbsp;&nbsp; 📊 No impact on credit score&nbsp;&nbsp;&nbsp;&nbsp; <br/>⭐ Trusted by leading lenders</p>
          </section>
          <FixedCta>
            <button id="business-loan-v2-otp-requested" className="business-v2-cta" disabled={!/^\d{10}$/.test(data.mobile) || !consent || loading} onClick={sendOtp}>Get OTP</button>
          </FixedCta>
        </>
      )}

      {screen === "otp" && (
        <>
          <section className="business-v2-page business-v2-scroll-page business-v2-otp-page">
            <Progress current="1/5" fill={56} onBack={goBack} />
            <h1>Verify Your Mobile Number</h1>
            <p className="business-v2-subtitle">Your data is encrypted &amp; safe</p>
            <p className="business-v2-otp-sent">OTP sent to +91 XXXXXX{data.mobile.slice(-4)}</p>
            <OTPInput
              value={otp}
              onChange={(value) => {
                setOtp(value);
                if (value.length === 4) verifyOtp(value);
              }}
              numInputs={4}
              isInputNum
              shouldAutoFocus
              containerStyle="business-v2-otp-inputs"
              inputStyle="business-v2-otp-input"
              renderInput={(props) => <input {...props} inputMode="numeric" pattern="[0-9]*" />}
            />
            <p className="business-v2-resend">
              Didn’t receive the OTP?&nbsp;&nbsp;
              {canResend ? <button onClick={resendOtp}>Resend OTP</button> : <strong>Resend in 00.{String(resendCounter).padStart(2, "0")} Sec</strong>}
            </p>
          </section>
          <FixedCta>
            <button className="business-v2-cta" disabled={otp.length !== 4 || loading} onClick={() => verifyOtp()}>Verify &amp; Continue</button>
          </FixedCta>
        </>
      )}

      {screen === "identity" && (
        <>
          <section className="business-v2-page business-v2-scroll-page business-v2-identity-page">
            <Progress current="1/5" fill={56} onBack={goBack} />
            <h1>Is this you?</h1>
            <p className="business-v2-subtitle">Review your pre-filled details to continue.</p>
            <p className="business-v2-label">Select your Gender</p>
            <div className="business-v2-pills">
              {["male", "female", "other"].map((value) => (
                <button key={value} type="button" className={data.gender === value ? "selected" : ""} onClick={() => updateData("gender", value)}>{value[0].toUpperCase() + value.slice(1)}</button>
              ))}
            </div>
            <Field label="Full Name" value={data.fullName} placeholder="Full name as per PAN" onChange={(value) => updateData("fullName", value)} />
            <Field label="Date of Birth" value={data.dob} placeholder="DD/MM/YYYY" onChange={(value) => updateData("dob", formatDateInput(value))} icon={<CalendarDays />} />
            <p className="business-v2-dob-help">Enter DOB in DD/MM/YYYY format — slashes added automatically</p>
            <Field label="Pan Card Number" value={data.pan} placeholder="ABCDE1234F" onChange={(value) => updateData("pan", value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))} />
            <Field label="Personal Email Address" value={data.email} placeholder="name@email.com" onChange={(value) => updateData("email", value)} />
            <Field label="Home Pin Code" value={data.pincode} placeholder="6-Digit Pin" onChange={(value) => updateData("pincode", value.replace(/\D/g, "").slice(0, 6))} icon={<BadgeCheck />} />
          </section>
          <FixedCta>
            <button id="business-loan-v2-personal-done" className="business-v2-cta" disabled={loading} onClick={saveIdentity}>Yes, that&apos;s me → Continue</button>
          </FixedCta>
        </>
      )}

      {screen === "loan" && (
        <>
          <section className="business-v2-page business-v2-scroll-page">
            <Progress current="2/5" fill={112} onBack={goBack} />
            <p className="business-v2-eyebrow">Loan Requirement</p>
            <h1>How much funding do you need?</h1>
            <div className="business-v2-option-list">
              {loanOptions.map((option) => (
                <button type="button" key={option.amount} className={data.loanAmount === option.amount ? "business-v2-option selected" : "business-v2-option"} onClick={() => { updateData("loanAmount", option.amount); updateData("loanPurpose", option.text); }}>
                  <span><strong>{option.label}</strong><small>{option.text}</small></span><i />
                </button>
              ))}
            </div>
          </section>
          <FixedCta>
            <button id="business-loan-v2-funding-done" className="business-v2-cta" disabled={!data.loanAmount || loading} onClick={saveLoan}>Next</button>
          </FixedCta>
        </>
      )}

      {screen === "turnover" && (
        <>
          <section className="business-v2-page business-v2-scroll-page">
            <Progress current="3/5" fill={128} onBack={goBack} />
            <p className="business-v2-eyebrow">Business Revenue</p>
            <h1>What&apos;s your annual turnover?</h1>
            <div className="business-v2-option-list business-v2-turnover-list">
              {turnoverOptions.map((option) => (
                <button type="button" key={option.value} className={data.annualTurnover === option.value ? "business-v2-option selected" : "business-v2-option"} onClick={() => updateData("annualTurnover", option.value)}>
                  <strong>{option.label}</strong><i />
                </button>
              ))}
            </div>
          </section>
          <FixedCta>
            <button id="business-loan-v2-annual-turnover" className="business-v2-cta" disabled={!data.annualTurnover || loading} onClick={saveTurnover}>Next</button>
          </FixedCta>
        </>
      )}

      {screen === "setup" && (
        <>
          <section className="business-v2-page business-v2-scroll-page">
            <Progress current="4/5" fill={169} onBack={goBack} />
            <p className="business-v2-eyebrow">Business Details</p>
            <h1>Tell us about your setup</h1>
            <ChoiceGroup
              className="business-v2-residence-group"
              title="Residence type"
              options={["Owned", "Rented", "Parental"]}
              icons={[Home, House, Users]}
              value={data.residenceType}
              onChange={(value) => updateData("residenceType", value)}
            />
            <ChoiceGroup
              className="business-v2-office-group"
              title="Office type"
              options={["Owned", "Rented", "Home Office", "Co-working"]}
              icons={[Home, House, Home, Laptop]}
              value={data.officeType}
              onChange={(value) => updateData("officeType", value)}
            />
          </section>
          <FixedCta>
            <button id="business-loan-v2-about-setup" className="business-v2-cta" disabled={!data.residenceType || !data.officeType || loading} onClick={saveSetup}>Next</button>
          </FixedCta>
        </>
      )}

      {screen === "verification" && (
        <>
          <section className="business-v2-page business-v2-scroll-page business-v2-gst-page">
            <Progress current="5/5" fill={225} onBack={goBack} />
            <p className="business-v2-eyebrow">Business Verification</p>
            <h1>How is your business registered?</h1>
            <ChoiceGroup className="business-v2-registration-choice" title="Do you have a GST Number?" options={["Yes", "No"]} value={data.gstAvailable} onChange={(value) => saveRegistrationChoice("gstAvailable", value)} />
            {data.gstAvailable === "Yes" && <Field label="Enter GST Number" value={data.gst} placeholder="e.g. 22AAAAA0000A1Z5" onChange={(value) => updateData("gst", value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15))} />}
          </section>
          <FixedCta>
            <button id="business-loan-v2-business-registered" className="business-v2-cta" disabled={data.gstAvailable !== "Yes" || !data.gst || loading} onClick={() => saveRegistrationNumber("gst")}>Check Eligibility</button>
          </FixedCta>
        </>
      )}

      {screen === "msme" && (
        <>
          <section className="business-v2-page business-v2-scroll-page business-v2-registration-page business-v2-msme-page">
            <Progress current="5/5" fill={225} onBack={goBack} />
            <p className="business-v2-eyebrow">Business Verification</p>
            <h1>How is your business registered?</h1>
            <ChoiceGroup className="business-v2-registration-choice" title="Do you have a GST Number?" options={["Yes", "No"]} value={data.gstAvailable} onChange={(value) => saveRegistrationChoice("gstAvailable", value)} />
            <ChoiceGroup className="business-v2-registration-choice" title="Is your business MSME registered?" options={["Yes", "No"]} value={data.msmeAvailable} onChange={(value) => saveRegistrationChoice("msmeAvailable", value)} />
            {data.msmeAvailable === "Yes" && <Field label="Enter MSME Registration Number" value={data.msme} placeholder="e.g. UDYAM-MH-00-0000000" onChange={(value) => updateData("msme", value.toUpperCase())} />}
          </section>
          <FixedCta>
            <button id="business-loan-v2-business-registered" className="business-v2-cta" disabled={data.msmeAvailable !== "Yes" || !data.msme || loading} onClick={() => saveRegistrationNumber("msme")}>Check Eligibility</button>
          </FixedCta>
        </>
      )}

      {screen === "bothNo" && (
        <>
          <section className="business-v2-page business-v2-scroll-page business-v2-registration-page">
            <Progress current="5/5" fill={225} onBack={goBack} />
            <p className="business-v2-eyebrow">Business Verification</p>
            <h1>How is your business registered?</h1>
            <ChoiceGroup className="business-v2-registration-choice" title="Do you have a GST Number?" options={["Yes", "No"]} value={data.gstAvailable} onChange={(value) => saveRegistrationChoice("gstAvailable", value)} />
            <ChoiceGroup className="business-v2-registration-choice" title="Is your business MSME registered?" options={["Yes", "No"]} value={data.msmeAvailable} onChange={(value) => saveRegistrationChoice("msmeAvailable", value)} />
          </section>
          <FixedCta>
            <button id="business-loan-v2-business-registered" className="business-v2-cta" disabled={loading} onClick={() => setScreen("companyDetails")}>Check Eligibility</button>
          </FixedCta>
        </>
      )}

      {screen === "companyConfirm" && (
        <>
          <section className="business-v2-page business-v2-scroll-page business-v2-company-confirm-page">
            <Progress current="3/4" fill={177} onBack={goBack} />
            <h1>{data.gstAvailable === "Yes" ? "Is this your GST Number" : "Is this your MSME Number"}</h1>
            <p className="business-v2-subtitle">Review your pre-filled details to continue.</p>
            <Field label={data.gstAvailable === "Yes" ? "Enter GST Number" : "Enter MSME Registration Number"} value={data.gstAvailable === "Yes" ? data.gst : data.msme} placeholder="Registration number" readOnly onChange={() => {}} />
            <Field label="Company Name" value={data.companyName} placeholder="Company name" readOnly onChange={() => {}} />
          </section>
          <FixedCta>
            <button id="business-loan-v2-business-details-submit" className="business-v2-cta" disabled={!data.companyName || loading} onClick={finishRegistration}>Check Eligibility</button>
          </FixedCta>
        </>
      )}

      {screen === "companyDetails" && (
        <>
          <section className="business-v2-page business-v2-scroll-page business-v2-company-details-page">
            <Progress current="5/5" fill={225} onBack={goBack} />
            <h1>Tell us about your business</h1>
            <p className="business-v2-subtitle">This helps us find the best loan offers for you.</p>
            <Field label="Company Name" value={data.companyName} placeholder="e.g. Abcd pvt. Ltd." onChange={(value) => updateData("companyName", value)} />
            <Field label="PAN Number" value={data.companyPan} placeholder="e.g. ABCDE1234F" onChange={(value) => updateData("companyPan", value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))} />
            <DropdownField label="Company type" value={data.companyType} placeholder="Select company type" options={companyTypeOptions} onChange={(value) => updateData("companyType", value)} />
            <Field label="Pin Code" value={data.companyPincode} placeholder="6-Digit Pin" onChange={(value) => updateData("companyPincode", value.replace(/\D/g, "").slice(0, 6))} />
            <Field label="Date of incorporation" value={data.incorporationDate} placeholder="DD/MM/YYYY" onChange={(value) => updateData("incorporationDate", formatDateInput(value))} />
            <DropdownField label="Business type" value={data.businessType} placeholder="Select business type" options={businessTypeOptions} onChange={(value) => updateData("businessType", value)} />
            <Field label="Business Address" value={[data.businessAddress, data.businessAddress2].filter(Boolean).join(", ")} placeholder="Enter business address..." onChange={(value) => setData((previous) => ({ ...previous, businessAddress: value, businessAddress2: "" }))} />
          </section>
          <FixedCta>
            <button id="business-loan-v2-business-details-submit" className="business-v2-cta" disabled={!data.companyName || !data.companyPan || !data.companyType || !data.companyPincode || !data.incorporationDate || !data.businessType || !data.businessAddress || loading} onClick={saveCompanyDetails}>Check Eligibility</button>
          </FixedCta>
        </>
      )}

      {screen === "normalOffers" && (
        <OfferResults
          type="normal"
          offers={offers}
          checkingMore={moreLendersChecking}
          onSelect={() => {
            trackEvent("whats-next-submit-business-loan-page-v2", data.mobile);
            setScreen("normalComplete");
          }}
        />
      )}

      {screen === "normalComplete" && <NormalComplete />}

      {screen === "goldOffers" && (
        <OfferResults
          type="gold"
          offers={offers}
          checkingMore={moreLendersChecking}
          onSelect={(offer) => {
            trackEvent(`offer-selected-business-loan-page-v2-${offer?.lender_name || "unknown"}`, data.mobile);
            setScreen("goldComplete");
          }}
        />
      )}

      {screen === "goldComplete" && <GoldComplete name={data.fullName} offers={offers} />}

      {screen === "loadingLookup" && (
        <section className="business-v2-loading-page business-v2-lookup-page">
          <p className="business-v2-loading-title">Fetching your business details…</p>
          <p className="business-v2-loading-subtitle">Checking GST &amp; MSME registrations for your mobile number.</p>
        </section>
      )}

      {screen === "loading" && (
        <section className="business-v2-loading-page">
          <p className="business-v2-loading-title">Finding your loan offers…</p>
          <p className="business-v2-loading-subtitle">Checking lenders that may suit your profile.</p>
          <div className="business-v2-loading-list">
            {["Mobile Verified", "Income Profile Matched", "Comparing 150+ Loan Programs", "Finding Best Offers", "Checking Instant Approval Chances"].map((item, index) => (
              <p key={item} className={index < loadingStep ? "is-done" : index === loadingStep ? "is-active" : ""}>
                {index < loadingStep ? (
                  <span className="business-v2-loading-check">✓</span>
                ) : index === loadingStep ? (
                  <span className="business-v2-loading-spinner" style={{ "--progress": `${loadingStepProgress * 360}deg` }} />
                ) : (
                  <span className="business-v2-loading-empty" />
                )}
                {item}
              </p>
            ))}
          </div>
          <div className="business-v2-loading-progress"><span style={{ width: `${((loadingStep + loadingStepProgress) / 5) * 100}%` }} /></div>
          <p className="business-v2-loading-complete">
            {stillChecking
              ? "Still checking with our lending partners, this can take a few more seconds…"
              : `${Math.round(((loadingStep + loadingStepProgress) / 5) * 100)}% Complete`}
          </p>
        </section>
      )}

      {screen === "noOffers" && (
        <section className="business-v2-results-page business-v2-no-offers-page">
          <div className="business-v2-results-content">
            <div className="business-v2-no-offers-body">
              <div className="business-v2-complete-icon business-v2-no-offers-icon">
                <span className="business-v2-complete-icon-ring" />
                <span className="business-v2-complete-icon-ring business-v2-complete-icon-ring-inner" />
                <span className="business-v2-complete-icon-core"><SearchX size={30} strokeWidth={3} /></span>
              </div>
              <h1 className="business-v2-no-offers-title">No offers currently</h1>
              <div className="business-v2-no-offers-card">
                <p>We checked with all our lending partners. None of them could offer you a business loan based on your current profile.</p>
              </div>
            </div>
          </div>
          <div className="business-v2-results-footer">
            <p className="business-v2-results-disclaimer">Approval and final terms are subject to lender verification and eligibility criteria. This is not a guarantee of loan approval.</p>
          </div>
        </section>
      )}
    </main>
  );
}

function OfferResults({ type, offers, checkingMore = false, onSelect }) {
  const isGold = type === "gold";
  return (
    <section className={`business-v2-results-page ${isGold ? "business-v2-gold-results" : "business-v2-normal-results"}`}>
      <div className="business-v2-results-content">
        {isGold ? (
          <>
            <h1><span>Sorry!</span> You are not eligible</h1>
            <p className="business-v2-results-intro">You don&apos;t currently qualify for Business Loan offers based on our partner lenders&apos; eligibility criteria.</p>
            <div className="business-v2-gold-callout">
              <img src="/assets/img/goldbricks.png" alt="" />
              <div>
                <p>But here&apos;s some good news!</p>
                <strong>You qualify for <span className="business-v2-gold-text">Gold Loan offers</span> from {offers.length} trusted lenders. Your offers are ready to compare with faster approval chances.</strong>
              </div>
            </div>
            <div className="business-v2-gold-timer">
              <span>⏱</span> Most customers complete the next step in under 3 minutes.
            </div>
            <div className="business-v2-gold-offer-list">
              {offers.map((offer) => <OfferCard key={offer._id} offer={offer} gold onSelect={onSelect} />)}
            </div>
            <div className="business-v2-results-footer-space" />
          </>
        ) : (
          <>
            <h1 className="business-v2-normal-title">🎉 Good News!<span>Your Business Matches Multiple Lenders</span></h1>
            <p className="business-v2-results-subtitle">Your application is under process with below lenders.</p>
            <div className="business-v2-gold-timer">
              <span>⏱</span> Most customers complete the next step in under 3 minutes.
            </div>
            <div className="business-v2-normal-offer-list">
              {offers.map((offer) => <OfferCard key={offer._id} offer={offer} onSelect={onSelect} />)}
            </div>
            <div className="business-v2-results-footer-space" />
          </>
        )}
      </div>
      <div className="business-v2-results-footer">
        {!isGold && (
          <div className="business-v2-results-footer-button">
            {checkingMore && <p className="business-v2-checking-more">Offers are being generated, please wait…</p>}
            <button className="business-v2-results-next" type="button" disabled={offers.length === 0} onClick={() => onSelect(offers[0])}>What&apos;s Next</button>
          </div>
        )}
        {isGold && checkingMore && (
          <div className="business-v2-results-footer-button">
            <p className="business-v2-checking-more">Offers are being generated, please wait…</p>
          </div>
        )}
        <p className="business-v2-results-disclaimer">{isGold ? "Disclaimer: These offers are subject to change at discretion of Bank / NBFC after receiving all your documents and details. Final offer will be based on risk policy of Bank / NBFC. We do not guarantee that final offer will be same as Pre-approved offer." : "Approval and final terms are subject to lender verification and eligibility criteria. This is not a guarantee of loan approval."}</p>
      </div>
    </section>
  );
}

function OfferCard({ offer, gold = false, onSelect }) {
  return (
    <article className={`business-v2-offer-card${gold ? " business-v2-gold-offer-card" : ""}`}>
      <div className="business-v2-offer-header">
        {offer.logo ? <img src={offer.logo} alt={`${offer.lender_name} logo`} /> : <span className="business-v2-offer-logo">{offer.lender_name.slice(0, 1)}</span>}
        <h2>{offer.lender_name}</h2>
      </div>
      {gold && (
        <div className="business-v2-offer-amount">
          <span>Loan Amount</span>
          <strong>{formatOfferValue(offer.credit_limit)}</strong>
        </div>
      )}
      <div className="business-v2-offer-details">
        {gold && <span>EMI / Month<strong>{formatOfferValue(offer.emi)}</strong></span>}
        {gold && <span>Tenure<strong>{offer.tenure}</strong></span>}
        {gold && <span>Interest<strong>{offer.interest_rate}</strong></span>}
        {!gold && <span>Tenure<strong>{offer.tenure}</strong></span>}
        {!gold && <span>Interest<strong>{offer.interest_rate}</strong></span>}
      </div>
      {gold && <button type="button" id="business-loan-v2-offers-selected" onClick={() => onSelect(offer)}>I like this</button>}
    </article>
  );
}

function NormalComplete() {
  return (
    <section className="business-v2-complete-page business-v2-normal-complete-page">
      <div className="business-v2-complete-icon">
        <span className="business-v2-complete-icon-ring" />
        <span className="business-v2-complete-icon-ring business-v2-complete-icon-ring-inner" />
        <span className="business-v2-complete-icon-core"><Check size={34} strokeWidth={3.5} /></span>
      </div>
      <h1>What happens next?</h1>
      <div className="business-v2-normal-message-card">
        <p className="business-v2-complete-message">You will receive call from our Lending partners for completing your application &amp; processing your Loan.</p>
      </div>
      <div className="business-v2-document-card">
        <h2>Keep your documents ready for quick processing</h2>
        <ul><li><span className="business-v2-black-check"><Check size={8} strokeWidth={4} /></span>Personal KYC Documents</li><li><span className="business-v2-black-check"><Check size={8} strokeWidth={4} /></span>Company KYC Documents</li><li><span className="business-v2-black-check"><Check size={8} strokeWidth={4} /></span>Latest 1 Year Bank Statements</li></ul>
      </div>
    </section>
  );
}

function GoldComplete({ name, offers }) {
  return (
    <section className="business-v2-complete-page business-v2-gold-complete-page">
      <h1>Thank you, {name || "there"}</h1>
      <p className="business-v2-gold-complete-subtitle">Your <span className="business-v2-gold-text">Gold Loan</span> request has been received</p>
      <p className="business-v2-gold-complete-message">📞 <strong>Keep your phone handy.</strong><br />Our lending partners will call you shortly to help you with your Gold Loan.</p>
      <div className="business-v2-gold-next-card">
        <h2>Next Steps</h2>
        <ul>
          <li><span className="business-v2-gold-check"><Check size={12} strokeWidth={3} /></span><span>You will receive a call from one of our lending partners shortly.</span></li>
          <li><span className="business-v2-gold-check"><Check size={12} strokeWidth={3} /></span><span>They&apos;ll guide you to a convenient branch near you.</span></li>
          <li><span className="business-v2-gold-check"><Check size={12} strokeWidth={3} /></span><span>Visit with your gold and required KYC documents.</span></li>
          <li><span className="business-v2-gold-check"><Check size={12} strokeWidth={3} /></span><span>Once your gold is evaluated and approved, the lender will process your loan.</span></li>
        </ul>
      </div>
      <div className="business-v2-gold-partners">
        {(offers || []).filter((offer) => offer.logo).map((offer) => <img key={offer.lender_id || offer._id} src={offer.logo} alt={offer.lender_name} />)}
      </div>
    </section>
  );
}

function formatOfferValue(value) {
  const stringValue = String(value || "");
  return stringValue.startsWith("₹") ? stringValue : stringValue ? `₹${stringValue}` : "-";
}

function FixedCta({ children }) {
  return <div className="business-v2-fixed-cta"><div>{children}</div></div>;
}

function Progress({ current, fill, onBack }) {
  return (
    <div className="business-v2-progress">
      <button type="button" className="business-v2-back" onClick={onBack} aria-label="Go back"><ArrowLeft size={14} strokeWidth={1.5} /></button>
      <div className="business-v2-progress-track"><span style={{ width: `${fill}px` }} /></div>
      <span className="business-v2-progress-count">{current}</span>
    </div>
  );
}

function Field({ label, value, placeholder, onChange, icon, readOnly = false, type = "text", inputMode }) {
  return (
    <label className={`business-v2-field${icon ? " has-icon" : ""}`}>
      <span>{label}</span>
      <input type={type} inputMode={inputMode} readOnly={readOnly} value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      {icon && <span className="business-v2-field-icon">{icon}</span>}
    </label>
  );
}

function DropdownField({ label, value, placeholder, options, onChange }) {
  const [open, setOpen] = useState(false);
  const currentValue = String(value || "").trim();
  return (
    <div className={`business-v2-dropdown${open ? " open" : ""}`}>
      <button type="button" className="business-v2-dropdown-field" aria-expanded={open} onClick={() => setOpen((isOpen) => !isOpen)}>
        <span>{label}</span>
        <span className={!currentValue ? "business-v2-dropdown-placeholder" : ""}>{currentValue || placeholder}</span>
        <ChevronDown size={18} strokeWidth={2} className="business-v2-dropdown-chevron" />
      </button>
      {open && (
        <div className="business-v2-dropdown-list">
          {options.map((option) => (
            <button
              type="button"
              key={option}
              className={option === currentValue ? "selected" : ""}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
            >
              <span>{option}</span>
              {option === currentValue && <Check size={16} strokeWidth={3} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChoiceGroup({ title, options, value, onChange, icons = [], className = "" }) {
  return (
    <div className={`business-v2-choice-group ${className}`}>
      <p>{title}</p>
      <div>
        {options.map((option, index) => {
          const Icon = icons[index];
          return (
            <button type="button" key={option} className={value === option ? "selected" : ""} onClick={() => onChange(option)}>
              {Icon && <Icon size={17} strokeWidth={1.5} />}
              <span>{option}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
